/**
 * Style helpers that let the ported markup keep the prototype's CSS strings verbatim.
 *
 * The prototype was authored against `dc-runtime`, which:
 *   - parsed the `style="…"` attribute into a React style object, and
 *   - turned `style-hover="…"` into a real `.class:hover { … !important }` rule.
 *
 * Both are reproduced here exactly, so a declaration copied out of the prototype
 * renders identically without being hand-translated to camelCase.
 */
import type { CSSProperties } from 'react'

/** Split a declaration list on `;`, ignoring semicolons inside quotes and url(). */
function splitDeclarations(css: string): string[] {
  const out: string[] = []
  let start = 0
  let quote: string | null = null
  let depth = 0
  for (let i = 0; i < css.length; i++) {
    const ch = css[i]
    if (quote) {
      if (ch === '\\') i++
      else if (ch === quote) quote = null
      continue
    }
    if (ch === '"' || ch === "'") quote = ch
    else if (ch === '(') depth++
    else if (ch === ')') depth = Math.max(0, depth - 1)
    else if (ch === ';' && depth === 0) {
      out.push(css.slice(start, i))
      start = i + 1
    }
  }
  out.push(css.slice(start))
  return out.map((d) => d.trim()).filter(Boolean)
}

/** `background-color` → `backgroundColor`, `-webkit-x` → `WebkitX`, `--x` stays as-is. */
function toReactKey(prop: string): string {
  if (prop.startsWith('--')) return prop
  const camel = prop.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
  return prop.startsWith('-') ? camel.charAt(0).toUpperCase() + camel.slice(1) : camel
}

const styleCache = new Map<string, CSSProperties>()

/**
 * Turn a CSS declaration string into a React style object.
 *
 * Results are memoised on the exact input string, so the repeated literals
 * throughout the pages cost one parse each for the life of the page.
 */
export function s(css: string): CSSProperties {
  const hit = styleCache.get(css)
  if (hit) return hit
  const obj: Record<string, string> = {}
  for (const decl of splitDeclarations(css)) {
    const i = decl.indexOf(':')
    if (i === -1) continue
    const prop = decl.slice(0, i).trim()
    const value = decl.slice(i + 1).trim()
    if (!prop || !value) continue
    obj[toReactKey(prop)] = value
  }
  const frozen = obj as CSSProperties
  styleCache.set(css, frozen)
  return frozen
}

/** Merge additional declarations over a base style object. */
export function sm(base: CSSProperties, css: string): CSSProperties {
  return { ...base, ...s(css) }
}

let sheet: CSSStyleSheet | null = null
let counter = 0
const pseudoCache = new Map<string, string>()

function ensureSheet(): CSSStyleSheet | null {
  if (typeof document === 'undefined') return null
  if (!sheet) {
    const el = document.createElement('style')
    el.setAttribute('data-sx', 'pseudo')
    document.head.appendChild(el)
    sheet = el.sheet
  }
  return sheet
}

/**
 * Every declaration is marked `!important` — same as dc-runtime — because the
 * element it lands on almost always carries a competing inline `style`, which
 * would otherwise win over a class rule regardless of the pseudo-class.
 */
function importantify(css: string): string {
  return splitDeclarations(css)
    .map((d) => (/!\s*important$/i.test(d) ? d : `${d} !important`))
    .join(';')
}

/**
 * Register a pseudo-class rule and return the class name to put on the element.
 * `h('color:#FFFFFF;')` is the port of `style-hover="color:#FFFFFF;"`.
 */
export function pseudo(name: string, css: string): string {
  const key = `${name}|${css}`
  const hit = pseudoCache.get(key)
  if (hit) return hit
  const cls = `sxp${(counter++).toString(36)}`
  const target = ensureSheet()
  if (target) {
    const isElement = name === 'before' || name === 'after'
    const selector = isElement ? `.${cls}::${name}` : `.${cls}:${name}`
    try {
      target.insertRule(`${selector}{${isElement ? css : importantify(css)}}`, target.cssRules.length)
    } catch {
      /* an unsupported declaration should never take the page down */
    }
  }
  pseudoCache.set(key, cls)
  return cls
}

/** Shorthand for the only pseudo-class the prototype uses. */
export function h(css: string): string {
  return pseudo('hover', css)
}
